import { useTranslation } from 'react-i18next'

import { Heading } from '@/components/ui/Heading'
import { cn } from '@/lib/utils'
import {
  PriceCurvesPlot,
  isPlottablePriceCurve,
  readCanonicalPriceCurve,
} from '@digital-twins/deck-ui/plots/price-curves'

import { CanonicalAnalysisOutput } from '@digital-twins/shared-types'

import { usePriceCurveBaseLine, usePriceCurvesLabels } from './usePriceCurvesLabels'

export interface PriceRangeCardProps {
  /** The analysis output's `priceCurve`; drawn only when it carries `reported` and `displayWindow`. */
  curve: CanonicalAnalysisOutput['priceCurve'] | undefined
  layout?: 'default' | 'stacked'
}

/**
 * The survey tab's one non-question card (plots plan G): the Van Westendorp
 * curves built from the four price questions, placed above them. Total only.
 *
 * Draws the canonical `PriceCurve` the analysis already published
 * (`analysisOutput.priceCurve`); a curve stored before `reported` and
 * `displayWindow` existed, or none at all, draws no card.
 */
export function PriceRangeCard(props: PriceRangeCardProps) {
  const { curve, layout = 'default' } = props

  const { t } = useTranslation('app')
  const labels = usePriceCurvesLabels()
  const baseLine = usePriceCurveBaseLine(isPlottablePriceCurve(curve) ? curve : { baseSize: 0, respondentCount: null })

  if (!isPlottablePriceCurve(curve)) {
    return null
  }
  const { data } = readCanonicalPriceCurve(curve)

  return (
    <section
      data-testid="survey-price-range-card"
      className={cn('mb-[42px] grid grid-cols-1 gap-3', layout !== 'stacked' && 'xl:grid-cols-[320px_minmax(0,1fr)] xl:gap-6')}
    >
      <div
        className={cn(
          'flex flex-col gap-1 rounded-2xl px-1',
          layout !== 'stacked' && 'xl:sticky xl:top-6 xl:self-start xl:bg-[hsl(var(--report-card))] xl:px-5 xl:py-4',
        )}
      >
        <Heading level={3} size="base" weight="semibold" className="font-sans text-foreground">
          {t('studyReport.priceRangeCardTitle')}
        </Heading>
        <span data-testid="survey-price-range-card-subtitle" className="text-sm text-muted-foreground leading-[1.6]">
          {t('studyReport.priceRangeCardSubtitle')}
        </span>
      </div>
      <div className="chart-app-surface rounded-2xl bg-[hsl(var(--report-card))] p-6">
        <PriceCurvesPlot
          data={{ ...data, labels }}
          props={{ size: 'report', fit: 'card' }}
          baseLine={baseLine}
          testIdPrefix="survey-price-range-plot"
        />
      </div>
    </section>
  )
}
